import { router } from '@inertiajs/react'
import Layout from '@/components/layout/Layout'
import TipTapEditor from '@/components/editor/TipTapEditor'
import { Plus, X, Loader2 } from 'lucide-react'
import { useState, useRef, useCallback, useEffect } from 'react'

interface Props {
  note: null
  all_tags: string[]
}

export default function NotesEdit({ all_tags }: Props) {
  const [title, setTitle] = useState('')
  const [bodyMarkdown, setBodyMarkdown] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [showTagInput, setShowTagInput] = useState(false)
  const [creating, setCreating] = useState(false)

  const titleInputRef = useRef<HTMLTextAreaElement>(null)
  const bodyRef = useRef('')
  const tagsRef = useRef<string[]>([])
  const createTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const createdRef = useRef(false)

  const resizeTitle = useCallback(() => {
    const el = titleInputRef.current
    if (el) {
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    }
  }, [])

  useEffect(() => {
    titleInputRef.current?.focus()
  }, [])

  const autoCreate = useCallback((titleValue: string) => {
    if (createdRef.current || !titleValue.trim()) return
    createdRef.current = true
    setCreating(true)

    router.post('/notes', {
      note: {
        title: titleValue.trim(),
        body_markdown: bodyRef.current,
        directory: 'wiki',
        tags: tagsRef.current,
      },
    }, {
      onError: () => {
        createdRef.current = false
        setCreating(false)
      },
    })
  }, [])

  function handleTitleInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value
    setTitle(value)
    resizeTitle()

    if (createTimeoutRef.current) clearTimeout(createTimeoutRef.current)
    if (value.trim()) {
      createTimeoutRef.current = setTimeout(() => autoCreate(value), 800)
    }
  }

  function handleTitleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (title.trim() && !createdRef.current) {
        if (createTimeoutRef.current) clearTimeout(createTimeoutRef.current)
        autoCreate(title)
      } else {
        const prosemirror = document.querySelector('.ProseMirror') as HTMLElement
        prosemirror?.focus()
      }
    }
  }

  function handleBodyChange(md: string) {
    setBodyMarkdown(md)
    bodyRef.current = md
  }

  function addTag(tag: string) {
    const trimmed = tag.trim().toLowerCase()
    if (trimmed && !tags.includes(trimmed)) {
      const updated = [...tags, trimmed]
      setTags(updated)
      tagsRef.current = updated
    }
    setTagInput('')
    setShowTagInput(false)
  }

  function removeTag(tag: string) {
    const updated = tags.filter(t => t !== tag)
    setTags(updated)
    tagsRef.current = updated
  }

  return (
    <Layout>
      <div className="relative min-h-[calc(100vh-4rem)]">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <button
              onClick={() => router.visit('/notes')}
              className="hover:text-gray-600 transition-colors"
            >
              Pages
            </button>
            <span className="text-gray-300">/</span>
            <span className="text-gray-500">New page</span>
            {creating && (
              <span className="ml-3 flex items-center gap-1 text-gray-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                Creating...
              </span>
            )}
          </div>
        </div>

        <textarea
          ref={titleInputRef}
          value={title}
          onChange={handleTitleInput}
          onKeyDown={handleTitleKeyDown}
          placeholder="Untitled"
          rows={1}
          disabled={creating}
          className="w-full resize-none bg-transparent text-[40px] font-bold leading-tight text-gray-900 outline-none border-none placeholder:text-gray-300 mb-1 disabled:opacity-50"
        />

        <div className="flex items-center gap-1.5 mb-8">
          {tags.map(tag => (
            <span
              key={tag}
              className="group inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs text-gray-500 bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              {tag}
              <button
                onClick={() => removeTag(tag)}
                className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-500"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}

          {showTagInput ? (
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault()
                  addTag(tagInput)
                }
                if (e.key === 'Escape') {
                  setShowTagInput(false)
                  setTagInput('')
                }
              }}
              onBlur={() => {
                if (tagInput.trim()) addTag(tagInput)
                else setShowTagInput(false)
              }}
              placeholder="Add tag..."
              list="tag-suggestions-new"
              className="w-24 rounded bg-white px-2 py-0.5 text-xs text-gray-700 outline-none placeholder:text-gray-400 border border-gray-200 focus:border-gray-400"
              autoFocus
            />
          ) : (
            <button
              onClick={() => setShowTagInput(true)}
              className="flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs text-gray-400 hover:text-gray-500 transition-colors"
            >
              <Plus className="h-3 w-3" />
            </button>
          )}

          <datalist id="tag-suggestions-new">
            {all_tags.filter(t => !tags.includes(t)).map(t => (
              <option key={t} value={t} />
            ))}
          </datalist>
        </div>

        <TipTapEditor
          content={bodyMarkdown}
          onChange={handleBodyChange}
          onSave={() => {
            if (title.trim() && !createdRef.current) {
              if (createTimeoutRef.current) clearTimeout(createTimeoutRef.current)
              autoCreate(title)
            }
          }}
          borderless
          showBubbleMenu
          showSlashCommands
          placeholder="Type '/' for commands, or just start writing..."
        />
      </div>
    </Layout>
  )
}
